<template>
  <div class="customer-profile-container bg-[#f4f6fc] min-h-[55vh] flex flex-col font-sans">
    <!-- Header -->
    <!-- <v-card-title class="bg-gray-200 dark-bg-custom flex justify-between w-full px-3 m-0">
      <span>Customer Details</span>
      <v-icon @click="closeCustomerPopUp">mdi-close</v-icon>
    </v-card-title> -->

    <!-- Content body -->
    <div class="p-6 flex-grow">
      <v-row>
        <!-- Left Sidebar: Profile Summary -->
        <v-col cols="12" md="3">
          <v-card class="pa-4 text-center rounded-xl mb-6 shadow-sm border-0" elevation="0">
            <div class="flex justify-center mb-4 mt-2">
              <v-avatar size="120" class="border-4 border-[#e3f2fd] shadow-sm">
                <!-- Fallback to a nice UI Faces avatar or generic icon -->
                <img
                  :src="`https://api.dicebear.com/7.x/initials/svg?seed=${
                    customer.firstName || 'User'
                  }`"
                  alt="Avatar"
                />
              </v-avatar>
            </div>
            <h2 class="text-lg font-bold text-gray-800 uppercase tracking-wide">
              {{
                customer.customerFullName ||
                `${customer.firstName || ''} ${customer.lastName || ''}`.trim() ||
                'N/A'
              }}
            </h2>
            <!-- <div class="text-sm font-semibold text-blue-600 mt-1 uppercase tracking-wider">
              INDIVIDUAL
            </div> -->
          </v-card>
        </v-col>

        <!-- Right Content: Details -->
        <v-col cols="12" md="9">
          <!-- Navigation Tabs -->
          <v-card elevation="0" class="rounded-xl mb-6 shadow-sm border-0 overflow-hidden">
            <v-tabs
              v-model="activeTab"
              color="primary"
              active-class="font-bold text-blue-700"
              slider-size="3"
              show-arrows
            >
              <v-tab class="normal-case tracking-normal">Basic Info</v-tab>
              <v-tab class="normal-case tracking-normal">Communication</v-tab>
              <v-tab class="normal-case tracking-normal">Employment And Income</v-tab>
              <v-tab class="normal-case tracking-normal">Identity Details</v-tab>
              <v-tab class="normal-case tracking-normal">Bank Details</v-tab>
              <v-tab class="normal-case tracking-normal">Loan Details</v-tab>
            </v-tabs>
          </v-card>

          <!-- Tab Contents -->
          <v-tabs-items v-model="activeTab" class="bg-transparent">
            <!-- 0: Basic Info -->
            <v-tab-item>
              <v-card elevation="0" class="rounded-xl pa-6 shadow-sm border-0 mb-6">
                <h3 class="text-lg font-bold text-[#5c729c] mb-6">Basic Information</h3>

                <v-row class="text-[#333]">
                  <v-col cols="12" sm="6">
                    <div class="grid grid-cols-[140px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">CIF ID</div>
                      <div>{{ customer.crn || 'N/A' }}</div>
                    </div>
                    <div class="grid grid-cols-[140px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">Date Of Birth</div>
                      <div>{{ formatDate(customer.dateOfBirth) }}</div>
                    </div>
                    <div class="grid grid-cols-[140px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">Mobile Number</div>
                      <div>{{ mask(customer.customerMobile?.[0]) || 'N/A' }}</div>
                    </div>
                    <div class="grid grid-cols-[140px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">Created By</div>
                      <div class="text-gray-700">
                        {{ formatUserName(customer.createdBy) }}
                      </div>
                    </div>
                    <div class="grid grid-cols-[140px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">Created On</div>
                      <div class="text-gray-700">{{ formatDateTime(customer.createdAt) }}</div>
                    </div>
                  </v-col>

                  <v-col cols="12" sm="6">
                    <div class="grid grid-cols-[150px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">Name</div>
                      <div class="uppercase">{{ customer.customerFullName || 'N/A' }}</div>
                    </div>
                    <div class="grid grid-cols-[150px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">Age</div>
                      <div>{{ calculateAge(customer.dateOfBirth) }}</div>
                    </div>
                    <div class="grid grid-cols-[150px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">Gender</div>
                      <div>{{ convertTitle(customer.gender) || 'N/A' }}</div>
                    </div>
                    <div class="grid grid-cols-[150px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">Father/Spouse Name</div>
                      <div class="uppercase">{{ customer.fatherName || 'N/A' }}</div>
                    </div>
                    <div class="grid grid-cols-[150px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">Updated By</div>
                      <div class="text-gray-700">
                        {{ formatUserName(customer.updatedBy) }}
                      </div>
                    </div>
                    <div class="grid grid-cols-[150px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">Updated On</div>
                      <div class="text-gray-700">{{ formatDateTime(customer.updatedAt) }}</div>
                    </div>
                  </v-col>
                </v-row>
              </v-card>
            </v-tab-item>

            <!-- 1: Communication -->
            <v-tab-item>
              <v-card elevation="0" class="rounded-xl pa-6 shadow-sm border-0 mb-6 min-h-[400px]">
                <h3 class="text-lg font-bold text-[#5c729c] mb-6">Communication Details</h3>

                <h4 class="font-bold text-gray-800 border-b pb-2 mb-4">Contact Info</h4>
                <v-row class="mb-6">
                  <v-col cols="12" sm="6">
                    <div class="grid grid-cols-[140px_1fr] gap-4 mb-3">
                      <div class="font-bold text-gray-600">
                        <v-icon small color="primary" class="mr-1">mdi-email</v-icon> Email
                      </div>
                      <div class="break-words">{{ customer.customerEmail || 'N/A' }}</div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <div
                      v-for="(num, idx) in customer.customerMobile"
                      :key="idx"
                      class="grid grid-cols-[140px_1fr] gap-4 mb-3"
                    >
                      <div class="font-bold text-gray-600">
                        <v-icon small color="primary" class="mr-1">mdi-phone</v-icon> Mobile
                        {{ idx + 1 }}
                      </div>
                      <div>{{ mask(num) }}</div>
                    </div>
                    <div
                      v-if="!customer.customerMobile || customer.customerMobile.length === 0"
                      class="text-gray-500 italic"
                    >
                      No mobile numbers provided.
                    </div>
                  </v-col>
                </v-row>

                <h4 class="font-bold text-gray-800 border-b pb-2 mb-4">Addresses</h4>
                <v-row v-if="customer.customerAddress && customer.customerAddress.length > 0">
                  <v-col
                    cols="12"
                    md="6"
                    v-for="address in customer.customerAddress"
                    :key="address._id"
                  >
                    <div class="border rounded-lg p-4 bg-gray-50 h-full">
                      <div class="font-bold text-primary mb-3 flex items-center justify-between">
                        <span>{{ convertTitle(address.typeOfAddress) }}</span>
                        <v-btn icon small color="blue" @click="openGoogleMaps(address)"
                          ><v-icon>mdi-map-marker-radius</v-icon></v-btn
                        >
                      </div>
                      <div class="text-gray-700 leading-relaxed">{{ extractAddress(address) }}</div>
                    </div>
                  </v-col>
                </v-row>
                <div v-else class="text-gray-500 italic">
                  No addresses registered for this customer.
                </div>
              </v-card>
            </v-tab-item>

            <!-- 2: Employment and Income -->
            <v-tab-item>
              <v-card elevation="0" class="rounded-xl pa-6 shadow-sm border-0 mb-6 min-h-[400px]">
                <h3 class="text-lg font-bold text-[#5c729c] mb-6">Employment & Income</h3>

                <div v-if="customer.employmentDetails">
                  <v-row>
                    <v-col cols="12" md="6">
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">Job Type</div>
                        <div class="text-gray-700">
                          {{ convertTitle(customer.employmentDetails.empType) || 'N/A' }}
                        </div>
                      </div>
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">Employer Name</div>
                        <div class="text-gray-700 uppercase">
                          {{ customer.employmentDetails.workHistory?.[0]?.employerName || 'N/A' }}
                        </div>
                      </div>
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">Designation</div>
                        <div class="text-gray-700">
                          {{ customer.employmentDetails.workHistory?.[0]?.designation || 'N/A' }}
                        </div>
                      </div>
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">Experience</div>
                        <div class="text-gray-700">
                          {{
                            customer.employmentDetails.totalWorkExperience
                              ? `${customer.employmentDetails.totalWorkExperience} Years`
                              : 'N/A'
                          }}
                        </div>
                      </div>
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">Joining Date</div>
                        <div class="text-gray-700">
                          {{ formatDate(customer.employmentDetails.joiningDate) }}
                        </div>
                      </div>
                    </v-col>

                    <v-col cols="12" md="6">
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">Monthly Income</div>
                        <div class="text-blue-700 font-bold">
                          {{
                            customer.employmentDetails.monthlyIncome
                              ? `₹ ${Number(
                                  customer.employmentDetails.monthlyIncome
                                ).toLocaleString()}`
                              : 'N/A'
                          }}
                        </div>
                      </div>
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">EMI Outgo</div>
                        <div class="text-red-600 font-bold">
                          {{
                            customer.employmentDetails.totalEMIAmountPerMonth
                              ? `₹ ${Number(
                                  customer.employmentDetails.totalEMIAmountPerMonth
                                ).toLocaleString()}`
                              : 'N/A'
                          }}
                        </div>
                      </div>
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4 border-t pt-4">
                        <div class="font-bold text-gray-800">Net Take Home</div>
                        <div class="text-green-700 font-bold text-lg">
                          {{
                            customer.employmentDetails.totalIncome
                              ? `₹ ${Number(
                                  customer.employmentDetails.totalIncome
                                ).toLocaleString()}`
                              : 'N/A'
                          }}
                        </div>
                      </div>
                    </v-col>
                  </v-row>

                  <!-- Employer Address Section -->
                  <div v-if="customer.employmentDetails.contact?.address" class="mt-6">
                    <h4 class="font-bold text-gray-800 border-b pb-2 mb-4">Employer Address</h4>
                    <div class="border rounded-lg p-4 bg-gray-50">
                      <div class="text-gray-700 leading-relaxed">
                        {{ extractAddress(customer.employmentDetails.contact.address) }}
                      </div>
                    </div>
                  </div>
                </div>

                <v-row v-else>
                  <v-col cols="12" md="6">
                    <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">Job Type</div>
                      <div class="text-gray-700">{{ convertTitle(customer.jobType) || 'N/A' }}</div>
                    </div>
                    <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">Occupation</div>
                      <div class="text-gray-700">{{ customer.occupation || 'N/A' }}</div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                      <div class="font-bold text-gray-800">Company Name</div>
                      <div class="text-gray-700">{{ customer.company || 'N/A' }}</div>
                    </div>
                    <div class="grid grid-cols-[160px_1fr] gap-4 mb-4 bg-green-50 p-2 rounded">
                      <div class="font-bold text-gray-800">Monthly Salary</div>
                      <div class="text-green-700 font-bold tracking-wide">
                        {{
                          customer.salary ? `₹ ${Number(customer.salary).toLocaleString()}` : 'N/A'
                        }}
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-card>
            </v-tab-item>

            <!-- 3: Identity Details -->
            <v-tab-item>
              <v-card elevation="0" class="rounded-xl pa-6 shadow-sm border-0 mb-6 min-h-[400px]">
                <div class="flex justify-between items-center mb-6">
                  <h3 class="text-lg font-bold text-[#5c729c]">Identity Documents</h3>
                </div>

                <div v-if="customer.identityDetails && customer.identityDetails.length > 0">
                  <v-row>
                    <v-col
                      cols="12"
                      v-for="(id, idx) in customer.identityDetails"
                      :key="idx"
                      class="mb-4"
                    >
                      <div
                        class="border rounded-xl p-5 bg-white shadow-sm hover:shadow-md transition-all"
                      >
                        <v-row align="center">
                          <v-col cols="12" sm="8">
                            <div class="flex items-center mb-3">
                              <v-icon color="primary" class="mr-2"
                                >mdi-card-account-details-outline</v-icon
                              >
                              <span class="text-lg font-bold text-gray-800">{{ id.idType }}</span>
                              <v-chip
                                small
                                class="ml-3 font-bold"
                                :color="id.isVerfied ? 'success' : 'warning'"
                                text-color="white"
                              >
                                {{ id.isVerfied ? 'VERIFIED' : 'PENDING VERIFICATION' }}
                                <v-icon right x-small>{{
                                  id.isVerfied ? 'mdi-check-decagram' : 'mdi-alert-circle-outline'
                                }}</v-icon>
                              </v-chip>
                            </div>

                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8">
                              <div class="flex flex-col">
                                <span
                                  class="text-xs text-gray-500 uppercase font-bold tracking-wider"
                                  >ID Number</span
                                >
                                <span class="text-gray-800 font-medium">{{
                                  id.idNumber || 'N/A'
                                }}</span>
                              </div>
                              <div class="flex flex-col">
                                <span
                                  class="text-xs text-gray-500 uppercase font-bold tracking-wider"
                                  >Name on ID</span
                                >
                                <span class="text-gray-800 font-medium">{{
                                  id.nameAsperId || 'N/A'
                                }}</span>
                              </div>
                              <div class="flex flex-col">
                                <span
                                  class="text-xs text-gray-500 uppercase font-bold tracking-wider"
                                  >Issued By</span
                                >
                                <span class="text-gray-800 font-medium">{{
                                  id.issuedBy || 'N/A'
                                }}</span>
                              </div>
                              <div class="flex flex-col">
                                <span
                                  class="text-xs text-gray-500 uppercase font-bold tracking-wider"
                                  >Validity</span
                                >
                                <span class="text-gray-800 font-medium">
                                  {{ formatDate(id.issueDate) }} to {{ formatDate(id.expiryDate) }}
                                </span>
                              </div>
                            </div>

                            <div
                              v-if="id.isVerfied"
                              class="mt-4 pt-3 border-t flex flex-wrap gap-4 text-xs text-green-700 bg-green-50 p-2 rounded-lg"
                            >
                              <div class="flex items-center">
                                <v-icon x-small color="success" class="mr-1"
                                  >mdi-account-check</v-icon
                                >
                                <span
                                  >Verified by:
                                  <strong>{{ id.verifiedBy || 'System' }}</strong></span
                                >
                              </div>
                              <div class="flex items-center">
                                <v-icon x-small color="success" class="mr-1"
                                  >mdi-calendar-check</v-icon
                                >
                                <span
                                  >Verified on:
                                  <strong>{{ formatDate(id.verifiedOn) }}</strong></span
                                >
                              </div>
                            </div>
                          </v-col>

                          <v-col cols="12" sm="4" class="text-right">
                            <v-btn
                              v-if="!id.isVerfied"
                              color="primary"
                              class="rounded-lg font-bold px-6 normal-case"
                              :loading="verifyingIndex === idx"
                              @click="verifyIdentity(idx)"
                            >
                              <v-icon left>mdi-check-circle</v-icon>
                              Verify Now
                            </v-btn>
                            <div v-else class="text-success flex flex-col items-end">
                              <v-icon size="48" color="success">mdi-shield-check</v-icon>
                              <span class="text-xs font-bold mt-1">Verified</span>
                            </div>
                          </v-col>
                        </v-row>
                      </div>
                    </v-col>
                  </v-row>
                </div>
                <div
                  v-else
                  class="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200"
                >
                  <v-icon size="64" color="gray-300">mdi-id-card</v-icon>
                  <p class="mt-4 text-gray-500 font-medium italic">
                    No identity documents provided.
                  </p>
                </div>
              </v-card>
            </v-tab-item>

            <!-- 4: Bank Details -->
            <v-tab-item>
              <v-card elevation="0" class="rounded-xl pa-6 shadow-sm border-0 mb-6 min-h-[400px]">
                <h3 class="text-lg font-bold text-[#5c729c] mb-6">Bank Information</h3>

                <div v-if="customer.bankDetails">
                  <v-row>
                    <v-col cols="12" md="6">
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">Bank Name</div>
                        <div class="text-gray-700 uppercase">
                          {{ customer.bankDetails.bankName || 'N/A' }}
                        </div>
                      </div>
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">Account Number</div>
                        <div class="text-gray-700 font-mono font-bold">
                          {{ customer.bankDetails.accountNumber || 'N/A' }}
                        </div>
                      </div>
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">IFSC Code</div>
                        <div class="text-blue-700 font-bold">
                          {{ customer.bankDetails.ifscCode || 'N/A' }}
                        </div>
                      </div>
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">Account Type</div>
                        <div class="text-gray-700">
                          {{ customer.bankDetails.accountType || 'N/A' }}
                        </div>
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">Branch Name</div>
                        <div class="text-gray-700 uppercase">
                          {{ customer.bankDetails.branchName || 'N/A' }}
                        </div>
                      </div>
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">MICR Code</div>
                        <div class="text-gray-700">
                          {{ customer.bankDetails.micrCode || 'N/A' }}
                        </div>
                      </div>
                      <div class="grid grid-cols-[160px_1fr] gap-4 mb-4">
                        <div class="font-bold text-gray-800">GSTN Number</div>
                        <div class="text-gray-700">
                          {{ customer.bankDetails.gstnNumber || 'N/A' }}
                        </div>
                      </div>
                      <div
                        v-if="customer.bankDetails.phoneNo"
                        class="grid grid-cols-[160px_1fr] gap-4 mb-4"
                      >
                        <div class="font-bold text-gray-800">Phone No</div>
                        <div class="text-gray-700">{{ customer.bankDetails.phoneNo }}</div>
                      </div>
                    </v-col>
                  </v-row>

                  <!-- Bank Address Section -->
                  <div v-if="customer.bankDetails.address" class="mt-4">
                    <h4 class="font-bold text-gray-800 border-b pb-2 mb-4">Bank Address</h4>
                    <div class="border rounded-lg p-4 bg-gray-50">
                      <div class="text-gray-700 leading-relaxed">
                        {{ extractAddress(customer.bankDetails.address) }}
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-else
                  class="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200"
                >
                  <v-icon size="64" color="gray-300">mdi-bank</v-icon>
                  <p class="mt-4 text-gray-500 font-medium italic">No bank details provided.</p>
                </div>
              </v-card>
            </v-tab-item>
            <!-- 5: Loan Details -->
            <v-tab-item>
              <v-card elevation="0" class="rounded-xl pa-6 shadow-sm border-0 mb-6 min-h-[400px]">
                <div class="flex justify-between items-center mb-6">
                  <h3 class="text-lg font-bold text-[#5c729c]">Loan History</h3>
                  <v-btn icon @click="fetchCustomerLoans" :loading="loadingLoans">
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </div>

                <div v-if="loadingLoans" class="flex justify-center py-12">
                  <v-progress-circular indeterminate color="primary"></v-progress-circular>
                </div>

                <div v-else-if="customerLoans && customerLoans.length > 0">
                  <v-simple-table>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left font-bold text-gray-600">Case No</th>
                          <th class="text-left font-bold text-gray-600">Type</th>
                          <th class="text-right font-bold text-gray-600">Sanctioned Amt</th>
                          <th class="text-center font-bold text-gray-600">Tenure</th>
                          <th class="text-center font-bold text-gray-600">Status</th>
                          <th class="text-center font-bold text-gray-600">KYC</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="loan in customerLoans" :key="loan._id" class="hover:bg-gray-50">
                          <td class="font-bold text-blue-700 text-lg">{{ loan.caseNo }}</td>
                          <td>{{ loan.loanDetails?.typeOfLoan || loan.caseType }}</td>
                          <td class="text-right">
                            ₹{{ (loan.loanDetails?.loanAmountSanctioned || 0).toLocaleString() }}
                          </td>
                          <td class="text-center">
                            {{ loan.loanDetails?.tenure || '-' }}
                            {{ loan.loanDetails?.tenureType || '' }}
                          </td>
                          <td class="text-center">
                            <v-chip
                              x-small
                              :color="loan.status === 'OPEN' ? 'success' : 'grey'"
                              text-color="white"
                            >
                              {{ loan.loanDetails?.currentStatus || loan.status }}
                            </v-chip>
                          </td>
                          <td class="text-center">
                            <v-icon
                              small
                              :color="
                                loan.loanDetails?.kycStatus === 'Verified' ? 'success' : 'warning'
                              "
                            >
                              {{
                                loan.loanDetails?.kycStatus === 'Verified'
                                  ? 'mdi-check-decagram'
                                  : 'mdi-alert-circle'
                              }}
                            </v-icon>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </div>
                <div
                  v-else
                  class="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200"
                >
                  <v-icon size="64" color="gray-300">mdi-text-box-search-outline</v-icon>
                  <p class="mt-4 text-gray-500 font-medium italic">
                    No loan records found for this customer.
                  </p>
                </div>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { getFullAddress, convertToTitleCase, maskPhoneNumber } from '@/utils/common'
import { axiosUrl } from '@/utils/workforceApi'

export default {
  name: 'CustomerDetailsCard',
  props: {
    customer: Object,
    closeCustomerPopUp: Function
  },
  data() {
    return {
      activeTab: 0,
      verifyingIndex: null,
      customerLoans: [],
      loadingLoans: false
    }
  },
  watch: {
    activeTab(newTab) {
      if (newTab === 5 && this.customerLoans.length === 0) {
        this.fetchCustomerLoans()
      }
    }
  },
  methods: {
    async fetchCustomerLoans() {
      if (!this.customer) return
      try {
        this.loadingLoans = true
        const customerName =
          this.customer.customerFullName ||
          `${this.customer.firstName || ''} ${this.customer.lastName || ''}`.trim()

        const response = await this.$axios.get('/collection/loan', {
          params: {
            customerSearch: customerName,
            limit: 100
          }
        })
        this.customerLoans = response.data?.loans || []
      } catch (error) {
        console.error('Error fetching customer loans:', error)
      } finally {
        this.loadingLoans = false
      }
    },
    extractAddress(str) {
      if (!str) return 'N/A'
      return getFullAddress(str)
    },
    convertTitle(str) {
      if (str) {
        return convertToTitleCase(str)
      }
      return 'N/A'
    },
    mask(num) {
      if (!num) return ''
      return maskPhoneNumber(num)
    },
    formatDate(date) {
      if (!date) return 'N/A'
      try {
        const d = new Date(date)
        return d
          .toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          })
          .replace(/\//g, '-')
      } catch (e) {
        return date
      }
    },
    formatDateTime(date) {
      if (!date) return 'N/A'
      try {
        const d = new Date(date)
        const dateStr = d
          .toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          })
          .replace(/\//g, '-')
        const timeStr = d.toLocaleTimeString('en-IN', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        })
        return `${dateStr} ${timeStr}`
      } catch (e) {
        return date
      }
    },
    formatUserName(userObj) {
      if (!userObj) return 'System'
      const systemUser = userObj.systemUserId || userObj
      const firstName = systemUser.firstName || ''
      const lastName = systemUser.lastName || ''
      const fullName = `${firstName} ${lastName}`.trim()
      return fullName || 'N/A'
    },
    calculateAge(date) {
      if (!date) return 'N/A'
      try {
        const d = new Date(date)
        const ageDifMs = Date.now() - d.getTime()
        const ageDate = new Date(ageDifMs)
        return Math.abs(ageDate.getUTCFullYear() - 1970)
      } catch (e) {
        return 'N/A'
      }
    },
    openGoogleMaps(address) {
      const fullAddress = getFullAddress(address)
      const encodedAddress = encodeURIComponent(fullAddress)
      const googleMapsURL = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`
      window.open(googleMapsURL, '_blank')
    },
    async verifyIdentity(index) {
      if (!this.customer?._id) return

      try {
        this.verifyingIndex = index
        const idDetail = this.customer.identityDetails[index]

        // We perform a deep clone of the customer object and update the identityDetails
        const customerPayload = JSON.parse(JSON.stringify(this.customer))
        const user = this.$storage.getUniversal('user')
        const userName =
          user?.fullName ||
          (user?.first_name ? `${user.first_name} ${user.last_name || ''}` : 'Admin')

        customerPayload.identityDetails[index].isVerfied = true
        customerPayload.identityDetails[index].verifiedBy = userName.trim()
        customerPayload.identityDetails[index].verifiedOn = new Date()

        await this.$axios.put(
          `${axiosUrl.customers.postCustomer}/${this.customer._id}`,
          customerPayload
        )

        // Update local state (reactive update)
        this.$set(this.customer.identityDetails, index, customerPayload.identityDetails[index])
        this.$toast?.success(`${idDetail.idType} verified successfully!`)
      } catch (error) {
        console.error('Verification failed', error)
        this.$toast?.error('Failed to verify identity document.')
      } finally {
        this.verifyingIndex = null
      }
    }
  }
}
</script>

<style scoped>
.customer-profile-container {
  max-width: 100%;
}

.customer-profile-container .v-tab {
  text-transform: none !important;
  font-weight: 500;
  letter-spacing: normal;
  color: #5c729c !important;
}

.customer-profile-container .v-tab--active {
  color: #1976d2 !important;
  font-weight: 700;
}
</style>
